// Rate limiting utility for external API calls

class RateLimiter {
    constructor(maxRequests = 10, timeWindow = 60000) { // 10 requests per minute by default
        this.maxRequests = maxRequests;
        this.timeWindow = timeWindow;
        this.requests = new Map();
    }

    /**
     * Check if a request can be made
     * @param {string} key - Unique identifier for the rate limit (e.g., 'gemini', 'microsoft-graph')
     * @returns {boolean} - True if request can be made
     */
    canMakeRequest(key) {
        const now = Date.now();
        const requestHistory = this.requests.get(key) || [];
        
        // Remove requests older than the time window
        const recentRequests = requestHistory.filter(timestamp => now - timestamp < this.timeWindow);
        
        if (recentRequests.length >= this.maxRequests) {
            return false;
        }
        
        // Add current request timestamp
        recentRequests.push(now);
        this.requests.set(key, recentRequests);
        
        return true;
    }

    /**
     * Get remaining requests for a key
     * @param {string} key - Unique identifier
     * @returns {number} - Number of remaining requests
     */
    getRemainingRequests(key) {
        const now = Date.now();
        const requestHistory = this.requests.get(key) || [];
        const recentRequests = requestHistory.filter(timestamp => now - timestamp < this.timeWindow);
        
        return Math.max(0, this.maxRequests - recentRequests.length);
    }

    /**
     * Get time until next request can be made
     * @param {string} key - Unique identifier
     * @returns {number} - Milliseconds until next request can be made
     */
    getTimeUntilNextRequest(key) {
        const now = Date.now();
        const requestHistory = this.requests.get(key) || [];
        const recentRequests = requestHistory.filter(timestamp => now - timestamp < this.timeWindow);
        
        if (recentRequests.length < this.maxRequests) {
            return 0;
        }
        
        const oldestRequest = Math.min(...recentRequests);
        return this.timeWindow - (now - oldestRequest);
    }

    /**
     * Clear all rate limit data
     */
    clear() {
        this.requests.clear();
    }
}

// Create rate limiters for different services
export const geminiRateLimiter = new RateLimiter(60, 60000); // 60 requests per minute for Gemini
export const microsoftGraphRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute for Microsoft Graph
export const exchangeRateLimiter = new RateLimiter(10, 60000); // 10 requests per minute for exchange rates

/**
 * Wrapper function for API calls with rate limiting
 * @param {Function} apiCall - The API function to call
 * @param {RateLimiter} rateLimiter - The rate limiter instance
 * @param {string} serviceName - Name of the service for error messages
 * @returns {Promise} - Promise that resolves with API response or rejects with rate limit error
 */
export const rateLimitedApiCall = async (apiCall, rateLimiter, serviceName = 'API') => {
    if (!rateLimiter.canMakeRequest(serviceName)) {
        const timeUntilNext = rateLimiter.getTimeUntilNextRequest(serviceName);
        const secondsUntilNext = Math.ceil(timeUntilNext / 1000);
        
        throw new Error(
            `Rate limit exceeded for ${serviceName}. Please wait ${secondsUntilNext} seconds before trying again.`
        );
    }
    
    try {
        return await apiCall();
    } catch (error) {
        // If it's a rate limit error from the API itself, update our rate limiter
        if (error.message && error.message.includes('rate limit')) {
            // Add a penalty request to our rate limiter
            const now = Date.now();
            const requestHistory = rateLimiter.requests.get(serviceName) || [];
            requestHistory.push(now);
            rateLimiter.requests.set(serviceName, requestHistory);
        }
        throw error;
    }
};

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} - Promise that resolves with function result
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            
            // Don't retry on rate limit errors
            if (error.message && error.message.includes('rate limit')) {
                throw error;
            }
            
            // Don't retry on validation errors
            if (error.message && error.message.includes('validation')) {
                throw error;
            }
            
            if (attempt === maxRetries) {
                throw error;
            }
            
            // Exponential backoff
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    
    throw lastError;
};

export default RateLimiter; 